import type { ComputedRef, EmitFn } from 'vue'
import { ref, inject, provide, type Ref, computed } from 'vue'
import { cloneDeep, keyBy } from 'lodash-es'
import type useConfirmation from '@lib/composables/useConfirmation'
import type {
  MetadataFilterRequest,
  UpsertPptxSlideImagesRequestPayload,
  UpsertPptxSlideImagesRequestParams,
  UpsertPptxSlideImagesResponse,
} from '@lib/types/core'
import useToast from '@lib/composables/useToast'
import type { CollectionPlayerWithPages, CollectionGroupWithPages } from '@ppt-conversion-selector/types/model.type'
import type { CollectionPage } from '@collection-player/types/collection-player.type'
import { findFirstSlide, generateNewCollection, getDefaultGroupName } from '@ppt-conversion-selector/utils/data.util'
import type { CanvasCreateRequest, CanvasRetrieve, PatchedCanvasUpdateRequest, Instance } from '@openapi'
import { type GetCanvasesParams } from '@lib/types/canvases'
import type { Emit } from '@ppt-conversion-selector/App.vue'
import { useCanvas } from '@ppt-conversion-selector/composables/canvas.use'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types.ts'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
type ExistingSections = Record<string, { id: string; saveAction: 'overwrite' | 'keep' }>

export type Api = {
  createCanvas: (params: Omit<CanvasCreateRequest, 'instance_id'>) => Promise<CanvasRetrieve>
  deleteCanvas: (id: string) => Promise<CanvasRetrieve>
  getCanvases: (params: GetCanvasesParams & { filters?: Record<string, any> }) => Promise<PaginatedData<CanvasRetrieve>>
  fetchMetadataFilter: (payload: MetadataFilterRequest) => Promise<Record<string, string | string[]>>
  upsertPptxSlideImages: (
    params: UpsertPptxSlideImagesRequestParams,
    body: UpsertPptxSlideImagesRequestPayload,
  ) => Promise<UpsertPptxSlideImagesResponse>
  updateCanvas: (
    payload: PatchedCanvasUpdateRequest & {
      id: CanvasRetrieve['id']
      instance_id?: Instance['id']
      fields?: string
    },
  ) => Promise<CanvasRetrieve>
  markFileAsConverted: (fileId: string) => Promise<void>
}

type StorePayload = {
  initialCollection?: CollectionPlayerWithPages
  t: (key: string, values?: Record<string, string | number>) => string
  confirmation: ReturnType<typeof useConfirmation>
  zIndex?: number
  instanceId: string
  api: Api
  emit: EmitFn<Emit>
  fileTags?: string[]
}

type StoreData = {
  collection: Ref<CollectionPlayerWithPages>
  expandedGroups: Ref<string[]>
  deleteGroup: (groupId: string, groupName: string) => void
  updateGroup: (group: Partial<Omit<CollectionGroupWithPages, 'id'>> & Pick<CollectionGroupWithPages, 'id'>) => void
  updateGroups: (groups: CollectionGroupWithPages[]) => void
  updateSlides: (groupId: string, slides: CollectionPage[]) => void
  updateCollectionName: (name: string) => void
  updateGroupName: (groupId: string, name: string) => void
  updateSlideName: (slideIndex: number, name: string) => void
  deleteSlide: (groupId: string, slide: CollectionPage) => void
  moveSlideToGroup: (fromGroup: string, toGroup: string, slide: CollectionPage) => void
  childModalZIndex?: number
  existingSections: Ref<ExistingSections>
  api: Api
  emit: EmitFn<Emit>
  isSaving: Ref<boolean>
  isInitialized: Ref<boolean>
  isDragDropDisabled: ComputedRef<boolean>
}

const saveAction = {
  overwrite: 'overwrite',
  keep: 'keep',
} as const

export type SlideMetadata = {
  title: string
  id: number
  sequence: number
}

const StoreKey = Symbol('PptConversionSelectorStore')

export function provideAppStore({
  initialCollection,
  t,
  confirmation,
  zIndex,
  api,
  instanceId,
  emit,
  fileTags,
}: StorePayload) {
  const isInitialized = ref(false)
  const isSaving = ref(false)
  const collection = ref<CollectionPlayerWithPages>({
    ...generateNewCollection(t),
    ...(cloneDeep(initialCollection) ?? {}),
  })
  const { toast } = useToast()
  const expandedGroups = ref<string[]>(collection.value.groups.map((group) => group.id))
  const existingSections = ref<ExistingSections>({})
  const { createSection, updateSection, saveTemplate, fetchCanvases, deleteCanvas } = useCanvas({
    t,
    api,
    collection,
    tags: fileTags,
  })

  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )

  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const isDragDropDisabled = computed(() => !!pitcherSettings.value?.generate_only_sections_in_ppt_converter)

  const initialize = async () => {
    const firstSlide = findFirstSlide(collection.value)

    if (!firstSlide) {
      throw new Error('No slides metadata found in the collection')
    }

    const { file } = firstSlide

    let _existingSections: Record<string, string> = {}

    try {
      _existingSections = (await api.fetchMetadataFilter({
        and: [{ key: 'pitcher_source_file_id', value: file.id }],
        key: 'pitcher_slide_id',
        values: file.metadata?.pptx_data?.slides?.map((slide: SlideMetadata) => slide.id),
        is_section: true,
        is_template: false,
        is_block: false,
        instance_id: instanceId,
      })) as Record<string, string>
    } catch (e: any) {
      if (e.status === 400) {
        toast.error(t('canvasUI.pptConversionSelector.metadataFetchError'))
      }

      throw e
    }

    const fetchedSections = await fetchCanvases(Object.values(_existingSections))

    existingSections.value = Object.keys(_existingSections ?? {}).reduce(
      (accumulator, key) => ({
        ...accumulator,
        [key]: {
          id: _existingSections[key],
          saveAction: fetchedSections?.results?.find((section) => section.id === _existingSections[key])?.metadata
            ?.pitcher_is_original_content
            ? saveAction.overwrite
            : saveAction.keep,
        },
      }),
      {},
    )

    isInitialized.value = true
  }

  const childModalZIndex = zIndex !== undefined ? zIndex + 1 : undefined

  const setCollection = (newCollection: CollectionPlayerWithPages) => {
    collection.value = newCollection
  }

  const updateGroup = (group: Partial<Omit<CollectionGroupWithPages, 'id'>> & Pick<CollectionGroupWithPages, 'id'>) => {
    setCollection({
      ...collection.value,
      groups: [
        ...collection.value.groups.map((_group) =>
          _group.id === group.id
            ? {
                ..._group,
                ...group,
              }
            : _group,
        ),
      ],
    })
  }

  const deleteGroup = (groupId: string, groupName: string) => {
    confirmation.danger({
      zIndex: childModalZIndex,
      hasExit: false,
      title: t('canvasUI.pptConversionSelector.deleteItemModal.title', { name: groupName }),
      content: t('canvasUI.pptConversionSelector.deleteItemModal.content', { name: groupName }),
      confirmText: t('canvasUI.pptConversionSelector.deleteItemModal.confirm'),
      cancelText: t('canvasUI.pptConversionSelector.deleteItemModal.cancel'),
      onConfirm: () => {
        const groups = collection.value.groups.filter((group) => group.id !== groupId)

        setCollection({
          ...collection.value,
          groups,
        })
      },
    })
  }

  const updateGroups = (groups: CollectionGroupWithPages[]) => {
    setCollection({
      ...collection.value,
      groups,
    })
  }

  const updateSlides = (groupId: string, slides: CollectionPage[]) => {
    setCollection({
      ...collection.value,
      groups: collection.value.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              slides,
            }
          : group,
      ),
    })
  }

  const updateCollectionName = (name: string) => {
    const trimmedName = name.trim()

    setCollection({
      ...collection.value,
      name: trimmedName || t('canvasUI.pptConversionSelector.defaultCollectionName'),
    })
  }

  const updateGroupName = (groupId: string, name: string) => {
    const trimmedName = name.trim()

    setCollection({
      ...collection.value,
      groups: collection.value.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              name: trimmedName || getDefaultGroupName(t),
            }
          : group,
      ),
    })
  }

  const updateSlideName = (slideIndex: number, name: string) => {
    const trimmedName = name.trim()

    setCollection({
      ...collection.value,
      groups: collection.value.groups.map((group) => ({
        ...group,
        slides: group.slides.map((slide) => ({
          ...slide,
          file: {
            ...slide.file,
            metadata: {
              ...(slide.file.metadata ?? {}),
              pptx_data: {
                ...(slide.file.metadata?.pptx_data ?? {}),
                slides: (slide.file.metadata?.pptx_data?.slides ?? []).map((_slide: SlideMetadata, index: number) =>
                  index === slideIndex
                    ? {
                        ...(_slide ?? {}),
                        title: trimmedName,
                      }
                    : _slide,
                ),
              },
            },
          },
        })),
      })),
    })
  }

  const deleteSlide = (groupId: string, slide: CollectionPage) => {
    confirmation.danger({
      zIndex: childModalZIndex,
      title: t('canvasUI.pptConversionSelector.deleteItemModal.title', {
        name: slide.file.name ?? '',
      }),
      content: t('canvasUI.pptConversionSelector.deleteItemModal.content', {
        name: slide.file.name ?? '',
      }),
      confirmText: t('canvasUI.pptConversionSelector.deleteItemModal.confirm'),
      cancelText: t('canvasUI.pptConversionSelector.deleteItemModal.cancel'),
      hasExit: false,
      onConfirm: async () => {
        const groups = collection.value.groups.map((group) => {
          if (group.id === groupId) {
            const slides = group.slides.filter((i) => i.id !== slide.id)

            return {
              ...group,
              slides,
            }
          }

          return group
        })

        updateGroups(groups)
      },
    })
  }

  const moveSlideToGroup = (fromGroup: string, toGroup: string, slide: CollectionPage) => {
    updateGroups(
      collection.value.groups.map((group) => {
        if (group.id === fromGroup) {
          return {
            ...group,
            slides: group.slides.filter((s) => s.id !== slide.id),
          }
        }

        if (group.id === toGroup) {
          return {
            ...group,
            slides: [...group.slides, slide],
          }
        }

        return group
      }),
    )
  }

  const savePitcherSlides = async (): Promise<CanvasRetrieve[] | false> => {
    const promises: Promise<CanvasRetrieve>[] = []
    const deletingPromises: Promise<CanvasRetrieve>[] = []
    const sectionIdsToFetch: string[] = []

    const firstSlide = findFirstSlide(collection.value)

    if (!firstSlide) {
      throw new Error('No slides found in the collection')
    }

    let images: UpsertPptxSlideImagesResponse | undefined = undefined

    if (!launchDarkly.value?.convert_pptx_to_section_with_scribble_pdfjs) {
      images = await api.upsertPptxSlideImages({ id: firstSlide.file.id }, { instance_id: instanceId })
    }

    const slidesToRemove = await confirmRemovingSlidesOrCancel(firstSlide.file.id)

    if (slidesToRemove === false) {
      return false
    }

    slidesToRemove.forEach((slideToRemove: string) => deletingPromises.push(deleteCanvas(slideToRemove)))

    collection.value.groups.forEach((group) => {
      group.slides.forEach((item) => {
        const details = item.file.metadata?.pptx_data?.slides?.[item.slide.index]

        if (!details) {
          throw new Error(`Slide details not found for item in index: ${item.slide.index}`)
        }

        const existingSectionDetails =
          existingSections.value[details.id] || existingSections.value[details.id.toString()]

        switch (existingSectionDetails?.saveAction) {
          case saveAction.keep:
            sectionIdsToFetch.push(existingSectionDetails.id)
            return
          case saveAction.overwrite:
            promises.push(
              updateSection(existingSectionDetails.id, item, details, group, images ? images[details.id] : undefined),
            )
            return
          default:
            promises.push(createSection(item, details, group, images ? images[details.id] : undefined))
            return
        }
      })
    })

    const fetchedSections = fetchCanvases(sectionIdsToFetch).then(({ results }) => results)
    const allSections = Promise.all([fetchedSections, ...promises])
    const deletedSections = Promise.all(deletingPromises)
    const markFileAsConverted = api.markFileAsConverted(firstSlide.file.id)

    return Promise.all([allSections, deletedSections, markFileAsConverted]).then(([sections]) => sections.flat())
  }

  const confirmRemovingSlidesOrCancel = async (fileId: string): Promise<string[] | false> => {
    const { [fileId]: allFileSlides } = (await api.fetchMetadataFilter({
      key: 'pitcher_source_file_id',
      values: [fileId],
      is_section: true,
      is_template: false,
      is_block: false,
      return_arrays: true,
      instance_id: instanceId,
    })) as Record<string, string[]>

    const usedSlides = Object.values(existingSections.value).map((section) => section.id)
    const slideToRemove = allFileSlides?.filter((existing) => !usedSlides.includes(existing)) ?? []

    if (slideToRemove.length > 0) {
      return await new Promise<false | string[]>((resolve) => {
        confirmation.danger({
          zIndex: childModalZIndex,
          hasExit: false,
          title: t('canvasUI.pptConversionSelector.deleteRemovedSectionsModal.title', { count: slideToRemove.length }),
          content: t('canvasUI.pptConversionSelector.deleteRemovedSectionsModal.content', {
            count: slideToRemove.length,
          }),
          confirmText: t('canvasUI.pptConversionSelector.deleteRemovedSectionsModal.confirm'),
          cancelText: t('canvasUI.pptConversionSelector.deleteRemovedSectionsModal.cancel'),
          onConfirm: () => resolve(slideToRemove),
          onCancel: () => resolve(false),
        })
      })
    }

    return []
  }

  const saveAndClose = async () => {
    isSaving.value = true

    try {
      const savedSlides = await savePitcherSlides()

      if (savedSlides === false) {
        return
      }

      if (!pitcherSettings.value?.generate_only_sections_in_ppt_converter) {
        const slidesById = keyBy(savedSlides, 'metadata.pitcher_slide_id')
        await saveTemplate(savedSlides, slidesById)
      }

      emit('close', collection.value)
    } finally {
      isSaving.value = false
    }
  }

  const storeData = {
    collection,
    expandedGroups,
    childModalZIndex,
    deleteGroup,
    updateGroup,
    updateGroups,
    updateSlides,
    updateCollectionName,
    updateGroupName,
    deleteSlide,
    moveSlideToGroup,
    existingSections,
    updateSlideName,
    api,
    initialize,
    emit,
    isInitialized,
    savePitcherSlides,
    saveAndClose,
    isSaving,
    isDragDropDisabled,
  }

  provide<StoreData>(StoreKey, storeData)

  return storeData
}

export function useAppStore() {
  const storeData = inject<StoreData>(StoreKey)

  if (!storeData) {
    throw new Error('useAppStore() is called without provider.')
  }

  return storeData
}
